import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable, timeout } from 'rxjs';
import { appEnv } from '../config/app-env';
import { extractTokenPairFromApiBody } from '../http/api-token-body.util';
import { ApiClient } from './api-client';
import { OperatorProfile } from './models/operator.models';

export type AdminTokenPair = { accessToken: string; refreshToken: string };

/** Operator authentication: `admin/AdminAccount/*`. */
@Injectable({ providedIn: 'root' })
export class AdminAccountApi {
  private readonly client = inject(ApiClient);
  // Bypasses every interceptor: a refresh must never be intercepted into another refresh.
  private readonly plainHttp = new HttpClient(inject(HttpBackend));

  async login(email: string, password: string): Promise<AdminTokenPair> {
    const data = await firstValueFrom(this.client.post<unknown>('admin/AdminAccount/login', { email, password }));
    const pair = extractTokenPairFromApiBody(data);
    if (!pair) {
      throw new Error('Invalid login response');
    }
    return pair;
  }

  /** Sends no bearer: the backend authorises refresh by `userId` + the stored refresh token. */
  async refresh(userId: string, refreshToken: string, acceptLanguage: string): Promise<AdminTokenPair> {
    const raw = await firstValueFrom(
      this.plainHttp.post<unknown>(
        `${appEnv.apiBaseUrl.replace(/\/+$/, '')}/admin/AdminAccount/refresh`,
        { userId, refreshToken },
        { headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'Accept-Language': acceptLanguage } },
      ),
    );
    const pair = extractTokenPairFromApiBody(raw);
    if (!pair) {
      throw new Error('Invalid refresh response');
    }
    return pair;
  }

  /** Requires the current bearer; revokes the operator's session server-side. A hung call must not trap the operator in the app. */
  async logout(): Promise<boolean> {
    return (await firstValueFrom(this.client.post<boolean>('admin/AdminAccount/logout').pipe(timeout(5000)))) === true;
  }

  /** The signed-in operator's profile and the permission keys its token roles hold. */
  me(): Observable<OperatorProfile> {
    return this.client.get<OperatorProfile>('admin/AdminAccount/me');
  }
}
